import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button as MuiButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { Button, useNotify } from 'react-admin';
import LocalPostOfficeIcon from '@mui/icons-material/LocalPostOffice';
import { SchedulePickupModal } from './SchedulePickupModal';
import { ScheduledPickup, cancelPickup, fetchUpcomingPickups } from './canadaPostPickupApi';
import { formatPickupDay, formatTime } from './pickupSchedule';

interface ScheduledPickupsContextValue {
  pickups: ScheduledPickup[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Opens the modal — with a pickup to modify it, without one to book a new one. */
  openScheduler: (pickup?: ScheduledPickup | null) => void;
}

const ScheduledPickupsContext = createContext<ScheduledPickupsContextValue | null>(null);

const useScheduledPickups = (): ScheduledPickupsContextValue => {
  const context = useContext(ScheduledPickupsContext);
  if (!context) {
    throw new Error('useScheduledPickups must be used inside <ScheduledPickupsProvider>');
  }
  return context;
};

/**
 * Holds the booked-pickup state for the deliveries page.
 *
 * The "Schedule pickup" button lives in the list's toolbar and the banner lives
 * above the rows — two subtrees react-admin renders separately — so the list and
 * the modal they share sit in a context above `<List>` rather than in either one.
 */
export const ScheduledPickupsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [pickups, setPickups] = useState<ScheduledPickup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ScheduledPickup | null>(null);

  const reload = useCallback(() => {
    setLoading(true);
    void fetchUpcomingPickups()
      .then((data) => {
        setPickups(data);
        setError(null);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Could not load scheduled pickups');
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => reload(), [reload]);

  const openScheduler = useCallback((pickup: ScheduledPickup | null = null) => {
    setEditing(pickup);
    setModalOpen(true);
  }, []);

  const value = useMemo(
    () => ({ pickups, loading, error, reload, openScheduler }),
    [pickups, loading, error, reload, openScheduler],
  );

  return (
    <ScheduledPickupsContext.Provider value={value}>
      {children}
      <SchedulePickupModal
        open={modalOpen}
        pickup={editing}
        onClose={() => {
          setModalOpen(false);
          setEditing(null);
        }}
        onSaved={reload}
      />
    </ScheduledPickupsContext.Provider>
  );
};

/** Toolbar button that opens the scheduler for a new pickup. */
export const SchedulePickupButton = () => {
  const { openScheduler } = useScheduledPickups();
  return (
    <Button
      label="Schedule Pickup"
      onClick={() => openScheduler(null)}
      startIcon={<LocalPostOfficeIcon />}
    />
  );
};

const PickupRow = ({ pickup }: { pickup: ScheduledPickup }) => {
  const { reload, openScheduler } = useScheduledPickups();
  const notify = useNotify();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await cancelPickup(pickup.id);
      notify('Pickup cancelled with Canada Post', { type: 'success' });
      setConfirmOpen(false);
      reload();
    } catch (err) {
      // Canada Post refuses once the driver is on the road; that reason is the
      // useful part, so show it rather than a generic failure.
      notify(
        `Could not cancel pickup: ${err instanceof Error ? err.message : 'unknown error'}`,
        { type: 'error' },
      );
    } finally {
      setCancelling(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
      >
        <Box>
          <Typography variant="body2">
            <strong>{formatPickupDay(pickup.pickupDate)}</strong>
            {` — ${formatTime(pickup.preferredTime)} to ${formatTime(pickup.closingTime)} at ${pickup.locationName}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[
              `Confirmation #${pickup.requestId}`,
              pickup.pickupVolume,
              pickup.pickupInstructions,
            ]
              .filter(Boolean)
              .join(' · ')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <MuiButton size="small" onClick={() => openScheduler(pickup)}>
            Modify
          </MuiButton>
          <MuiButton size="small" color="error" onClick={() => setConfirmOpen(true)}>
            Cancel
          </MuiButton>
        </Stack>
      </Stack>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Cancel this Canada Post pickup?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {`Canada Post will be told not to come to ${pickup.locationName} on ${formatPickupDay(pickup.pickupDate)}. `}
            They can refuse once the driver has been dispatched.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={() => setConfirmOpen(false)} disabled={cancelling}>
            Keep it
          </MuiButton>
          <MuiButton
            color="error"
            onClick={() => void handleCancel()}
            disabled={cancelling}
            startIcon={cancelling ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {cancelling ? 'Cancelling…' : 'Cancel pickup'}
          </MuiButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

/**
 * Sits above the delivery rows: what Canada Post has been asked to collect and
 * when. Silent when nothing is booked — an empty "no pickups" box every day
 * would just be furniture.
 */
export const ScheduledPickupsBanner = () => {
  const { pickups, loading, error } = useScheduledPickups();

  if (error) {
    return (
      <Alert severity="warning" sx={{ mx: 2, mt: 2 }}>
        {`Could not load scheduled Canada Post pickups: ${error}`}
      </Alert>
    );
  }
  if (loading || pickups.length === 0) return null;

  return (
    <Alert severity="info" icon={<LocalPostOfficeIcon />} sx={{ mx: 2, mt: 2 }}>
      <AlertTitle>
        {pickups.length === 1 ? 'Canada Post pickup scheduled' : 'Canada Post pickups scheduled'}
      </AlertTitle>
      <Stack spacing={1.5} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
        {pickups.map((pickup) => (
          <PickupRow key={pickup.id} pickup={pickup} />
        ))}
      </Stack>
    </Alert>
  );
};
